import { v4 } from 'uuid';
import { useDispatch, useSelector } from 'react-redux';
import { locales } from '@americanexpress/conditional-features';
import { getIsLoggedInSelector, getActiveLocaleSelector } from '@americanexpress/travel-selectors';
import { ignoreHeadersByKey, useFetchye } from 'fetchye';
import { addErrorToReport } from '@americanexpress/one-app-ducks';
import { TRAVELER_V2_API_ENDPOINT } from '../constants/travelerDataConstants';
import { travelerV2APIBaseUrlSelector } from '../selectors/gettravelerV2ApiUrlBaseSelector';
import { logoutLinkSelector } from '../selectors/getNavSelectors';
import { MODULE_NAME } from '../constants/modules';

// this should be replaced by the @americanexpress/fetchye-amex library once it is available

export const makeErrorOnNotOkFetcher = (
  dispatch,
  reporting
) => async (fetchClient, key, options) => {
  let ok;
  let status;
  let responseBody;
  let error;
  let responseError;
  let responseHeaders;
  try {
    const res = await fetchClient(key, options);
    ok = res.ok;
    status = res.status;
    responseBody = await res.text();
    responseHeaders = Object.fromEntries([...res.headers]);

    if (!ok) {
      responseError = new Error(`Fetch Error: status: ${status}`);
    }

    try {
      responseBody = JSON.parse(responseBody);
    } catch {
      // responseBody will still be the text from the response, so no action needed here
    }
  } catch (requestError) /* istanbul ignore next */ {
    // eslint-disable-next-line no-console -- logging error for debugging
    console.error(requestError);
    error = requestError;
  }

  const errorToReport = error || responseError;
  if (reporting && errorToReport) {
    dispatch(addErrorToReport(
      errorToReport,
      {
        collectionMethod: 'Fetch Error',
        ...reporting,
      }
    ));
  }

  return {
    payload: {
      ok,
      status,
      body: responseBody,
      headers: responseHeaders,
      responseError,
    },
    error,
  };
};

export const useTravelerData = () => {
  const dispatch = useDispatch();
  const logoutLink = useSelector(logoutLinkSelector);
  const isLoggedIn = useSelector(getIsLoggedInSelector);
  const locale = useSelector(getActiveLocaleSelector);
  const isUsMarket = locale === locales.EN_US;

  const travelerV2ApiBaseUrl = useSelector(travelerV2APIBaseUrlSelector);

  const travelerEndpoint = `${travelerV2ApiBaseUrl}${TRAVELER_V2_API_ENDPOINT}?locale=${locale}&x-tls-encryption=hiped`;

  const fetcherOpts = {
    defer: !isLoggedIn || isUsMarket,
    method: 'GET',
    credentials: 'include',
    headers: () => ({
      'Content-Type': 'application/json',
      correlation_id: v4(),
    }),
    mapOptionsToKey: ignoreHeadersByKey(['correlationId', 'correlation_id']),
  };
  const errorReporting = {
    moduleID: MODULE_NAME,
    fileName: 'src/hooks/useTravelerData.js',
    endpoint: travelerEndpoint,
  };
  const { isLoading, data, error } = useFetchye(
    travelerEndpoint, fetcherOpts, makeErrorOnNotOkFetcher(dispatch, errorReporting));

  if (global?.BROWSER) {
    // Redirect to guest experience if session is expired
    if (isLoggedIn && data?.status === 401 && !window.location.href.includes(logoutLink)) {
      window.location.href = `${logoutLink}?DestPage=${window.location.href}`;
    }
  }

  return {
    travelerIsLoading: isLoading,
    travelerData: data?.body,
    travelerError: error || data?.responseError,
  };
};
